/**
 * The painted characters: a port of apps/display/public/avatar-idle.js (V12).
 *
 * A child is one complete painted frame at a time, recoloured for that child.
 * Displacement fields interpolate adjacent poses, with continuous breathing,
 * blinking and -- for the standing portrait -- a slow sway.
 *
 * ONE DIFFERENCE FROM THE DISPLAY, and it is where the pictures come from. The
 * display splits each painting into skin, clothes, props, brow and tongue
 * layers on a canvas when it loads, and hands the SVG data URLs. Here that
 * split was done once by tools/art_characters.py, and the SVG points at the
 * layer files it wrote. Everything else -- the markup, the filters, the head
 * and eye overlays, the crop, the timing -- is the display's, and
 * painted.parity.test.ts renders through the display's own file and this one
 * and requires the same SVG.
 *
 * Layer data is available immediately. React consumers decode the complete
 * loop before swapping SVGs, keeping the previous painting visible until all
 * replacement images are ready.
 */

import { load } from '../valley/loader';
import { CHARACTER_ART } from '../valley/art.generated';
import { assetUrl } from '../valley/sprites';
import { catalogue, normalise, type AvatarSpec } from './avatar';
import { BY_ID, FIRST, hash, outfitOf, type Activity } from './activities';

let serial = 0;

// ---------------------------------------------------------------------------
// The art, as the display's `ready` map would hold it
// ---------------------------------------------------------------------------

interface Frame {
  w: number;
  h: number;
  skin: string | null;
  garment: string | null;
  props: string | null;
  brows: string | null;
  tongue: string | null;
  companion: string | null;
}

interface Ready {
  frames: Frame[];
  placeholder: boolean;
  activity: Activity;
  outfit: number;
}

const readyCache = new Map<string, Ready | null>();

/** The painted data for one activity and outfit, or null if it was not painted. */
export function artFor(activityId: string, outfit: number): Ready | null {
  const key = `${activityId}/${outfit}`;
  if (readyCache.has(key)) return readyCache.get(key)!;
  const art = CHARACTER_ART[activityId];
  const base = BY_ID[activityId];
  const frames = art?.frames[String(outfit)];
  let value: Ready | null = null;
  if (art && base && frames && frames.length) {
    const url = (name: string | null | undefined) => (name ? assetUrl(name, 'webp') : null);
    value = {
      frames: frames.map((f) => ({
        w: art.width, h: art.height,
        skin: url(f.skin), garment: url(f.garment), props: url(f.props),
        brows: url(f.brows), tongue: url(f.tongue),
        companion: url(f.companion),
      })),
      placeholder: false,
      // `load` merges the painted activity.json over the registry entry.
      activity: { ...base, ...(art.meta as Partial<Activity>), ...(art.meta as Partial<Activity>).outfitFits?.[String(outfit)] } as Activity,
      outfit,
    };
  }
  readyCache.set(key, value);
  return value;
}

/** Whether an activity has paintings at all. */
export function isPainted(activityId: string): boolean {
  return Boolean(CHARACTER_ART[activityId]);
}

function hairUrl(activityId: string, style: number): string {
  const name = CHARACTER_ART[activityId]?.hair[String(style)];
  return name ? assetUrl(name, 'webp') : '';
}

// Through the valley's one queue (valley/loader.ts), so a character's frames
// take their turn behind the painting instead of arriving all at once.
function decodeAsset(url: string): Promise<void> {
  return load(url, 1);
}

/** Decode the complete loop before swapping a character or starting playback. */
export async function preload(spec: Partial<AvatarSpec> | null | undefined, options: PaintedOptions = {}): Promise<void> {
  const asked = options.activity;
  const activity = options.headOnly ? 'standing' : typeof asked === 'string' ? asked : asked?.id ?? FIRST[0]!;
  const data = artFor(activity, options.headOnly ? 0 : outfitOf(spec));
  if (!data) return;
  const s = normalise(spec);
  const frames = options.headOnly ? data.frames.slice(0, 1) : data.frames;
  const urls = frames.flatMap(f => [f.skin, f.garment, f.props, f.brows, f.tongue, f.companion]);
  if (data.activity.hairStyles?.includes(s.hair)) urls.push(hairUrl(activity, s.hair));
  if (!options.headOnly) Object.values(data.activity.transitions ?? {}).forEach(pair => {
    urls.push(motionUrl(activity, pair.forward, data.activity.motionRoot), motionUrl(activity, pair.backward, data.activity.motionRoot));
  });
  await Promise.all([...new Set(urls.filter((url): url is string => Boolean(url)))].map(decodeAsset));
}

/** Raw thumbnail SVGs also wait for every layer, avoiding loose eyes or hair. */
function prepareSvg(svg: SVGSVGElement): void {
  if (svg.dataset.artReady === 'true') return;
  svg.style.visibility = 'hidden';
  const urls = [...new Set([...svg.querySelectorAll('image,feImage')].map(el => el.getAttribute('href')).filter((url): url is string => Boolean(url)))];
  const data = artFor(svg.dataset.activity ?? '', +(svg.dataset.outfit ?? 0));
  if (svg.dataset.head !== 'true') Object.values(data?.activity.transitions ?? {}).forEach(pair => {
    urls.push(motionUrl(svg.dataset.activity!, pair.forward, data?.activity.motionRoot), motionUrl(svg.dataset.activity!, pair.backward, data?.activity.motionRoot));
  });
  Promise.all(urls.map(decodeAsset)).then(() => {
    svg.dataset.artReady = 'true';
    svg.style.visibility = '';
  }).catch(() => {
    // Show what did load rather than nothing (audit, 2026-10-12).
    svg.dataset.artReady = 'error';
    svg.style.visibility = '';
  });
}

// ---------------------------------------------------------------------------
// Tint, shared shape with the rest of the display
// ---------------------------------------------------------------------------

function tint(id: string, color: string, gain = 1, seal = false): string {
  const rgb = (color.match(/[a-f\d]{2}/gi) ?? []).map((n) => (parseInt(n, 16) / 255) * (gain > 2 ? 1 : gain));
  return `<filter id="${id}" color-interpolation-filters="sRGB">${seal ? '<feMorphology operator="dilate" radius="1.1"/>' : ''}<feColorMatrix type="saturate" values="0"/>${gain > 2 ? `<feComponentTransfer>${['R', 'G', 'B'].map((c) => `<feFunc${c} type="gamma" amplitude="1.45" exponent="0.65" offset="0"/>`).join('')}</feComponentTransfer>` : ''}<feComponentTransfer>${rgb.map((n, i) => `<feFunc${'RGB'[i]} type="linear" slope="${n}" intercept="0.015"/>`).join('')}</feComponentTransfer></filter>`;
}

function motionUrl(activity: string, file: string, root = 'avatar-motion-v23'): string {
  return `/assets/${root}/${activity}/${file}`;
}

// ---------------------------------------------------------------------------
// Head overlays: eyes, placeholder hair, extras
// ---------------------------------------------------------------------------

export function headOverlay(spec: AvatarSpec, activity: Activity, uid: string, hairColor: string, eyeColor: string): string {
  const e = activity.eyes, ink = '#513727';
  const hair = `<g data-bone="hair" data-placeholder="hair-v12" data-spec="V12 hair: painted onto the turnaround head, then differenced off. 8 styles x 3 camera angles.">` +
    `<path d="M6 46 C2 18 22 2 50 2 C78 2 98 18 94 48 C88 26 74 16 50 16 C26 16 12 26 6 46 Z" fill="${hairColor}" opacity=".92"/>` +
    ([3, 4, 6, 7].includes(spec.hair) ? `<path d="M8 40 C0 70 4 104 12 118 C18 126 28 124 28 112 C24 88 18 62 16 44 Z" fill="${hairColor}" opacity=".92"/>` +
      `<path d="M92 40 C100 70 96 104 88 118 C82 126 72 124 72 112 C76 88 82 62 84 44 Z" fill="${hairColor}" opacity=".92"/>` : '') +
    `</g>`;
  const closed = activity.expression === 'asleep';
  const skin = catalogue.skin!.swatches![spec.skin]!;
  const eye = (px: number, py: number, index: number) => {
    if (closed) return `<path d="M${px * 100 - 6} ${py * 100}q6 4 12 0" fill="none" stroke="${ink}" stroke-width=".85"/>`;
    const eyeId = uid + 'eye' + index;
    const outline = 'M-6 0 C-6-8.6 5.8-8.8 6-.4 C6 8.5-5.8 8.7-6 0Z';
    const gaze = activity.interaction === 'stroke-rabbit' ? [-.45, .65] : activity.interaction === 'read-book' ? [0,.35] : activity.gaze;
    const eyeScale = activity.eyeScales?.[index]?.join(' ') ?? activity.eyeScale?.join(' ') ?? (activity.id === 'standing' ? '1.3 1.35' : '1.15 1.2');
    const socketScale = activity.eyeScales?.[index] ?? activity.eyeScale ?? [1.15, 1.2];
    // The rabbit painting has taller sockets than the visible eye curve.
    // Cover its original rim separately; compensate for each socket's stretch
    // so the same eye drawing as the other activities stays round on the face.
    const roundScale = .95 * socketScale[0]! / socketScale[1]! * 12 / 13;
    const socketSkin = `rgb(${(skin.match(/[a-f\d]{2}/gi) ?? []).map(n => Math.round(parseInt(n, 16) * .916 + 3.825)).join(' ')})`;
    const irisInk = '#302820';
    const eyeAngle = activity.eyeAngles?.[index] ?? 0;
    return `<g data-eye-index="${index}" transform="translate(${px * 100} ${py * 100})${eyeAngle ? ` rotate(${eyeAngle})` : ''} scale(${eyeScale})">
        <defs>
        <clipPath id="${eyeId}shape"><path d="${outline}"/></clipPath>
        <clipPath id="${eyeId}blink"><rect data-eye-reveal="" x="-8" y="-7" width="16" height="14"/></clipPath></defs>
        ${activity.interaction === 'stroke-rabbit' ? `<ellipse data-eye-socket="" rx="7" ry="10.4" fill="${socketSkin}"/>` : `<path d="${outline}" fill="${skin}"/>`}
        ${activity.interaction === 'stroke-rabbit' ? `<g data-eye-fit="round" transform="scale(.95 ${roundScale})">` : ''}
        <g clip-path="url(#${eyeId}shape)"><g clip-path="url(#${eyeId}blink)">
          <path d="${outline}" fill="#f4eddc"/>
          ${gaze ? `<g data-gaze="${activity.interaction === 'stroke-rabbit' ? 'rabbit' : activity.interaction === 'read-book' ? 'reading' : 'fixed'}" transform="translate(${gaze.join(' ')})">` : ''}<path d="M-4.25-4.5 Q-3.8-7 0-7 Q4.1-7 4.4-3.6 L4.1 3.5 Q3.5 6.5 .1 6.7 Q-3.8 6.5-4.2 3.2Z" fill="${irisInk}"/>
          <path d="M-3.35 2.3 Q0 4.2 3.35 2.1 L2.9 4.35 Q.1 6.15-2.8 4.4Z" fill="${eyeColor}" opacity=".7"/>
          <path d="M-2.2-3.9 Q-3.2-2.6-2-1.8 Q-.7-1.8-.9-3.1 Q-1-4-2.2-3.9Z" fill="#fff6df"/>${gaze ? '</g>' : ''}
        </g></g>
        <path data-upper-lid="" d="M-6 0 C-6-8.6 5.8-8.8 6-.4" fill="none" stroke="#49372b" stroke-width="1.05" stroke-linecap="round"/>${activity.interaction === 'stroke-rabbit' ? '</g>' : ''}
      </g>`;
  };
  const eyes = `<g data-bone="eyes" class="avatar-eyes">${e.map((point, index) => eye(point[0], point[1], index)).join('')}</g>`;
  const eyePoint = (index: number, dx: number, dy: number) => {
    const angle=(activity.eyeAngles?.[index]??0)*Math.PI/180;
    return [e[index]![0]*100+dx*Math.cos(angle)-dy*Math.sin(angle),e[index]![1]*100+dx*Math.sin(angle)+dy*Math.cos(angle)];
  };
  let extra = '';
  if (spec.extra === 1 || spec.extra === 2) {
    const armDirection=e[0]![0]>.5?-1:1;
    const bridge=e.length>1?`M${eyePoint(0,12,0).join(' ')}L${eyePoint(1,-12,0).join(' ')}`
      :`M${eyePoint(0,armDirection*12,-2).join(' ')}L${eyePoint(0,armDirection*31,-5).join(' ')}`;
    extra = `<g data-extra="glasses" fill="none" stroke="#29211c" stroke-width="2.3">${e.map(([x, y],index) => `<rect x="${x * 100 - 12}" y="${y * 100 - 10.5}" width="24" height="21" rx="${spec.extra === 2 ? 10.5 : 5}"${activity.eyeAngles?.[index]?` transform="rotate(${activity.eyeAngles[index]} ${x*100} ${y*100})"`:''}/>`).join('')}<path d="${bridge}"/></g>`;
  }
  if (spec.extra === 3) {
    const dots = e.flatMap((_,index) => [[-4, 11], [1, 13], [6, 10]].map(([dx, dy]) => eyePoint(index,dx!,dy!)));
    if (e.length === 2) dots.push([(e[0]![0] + e[1]![0]) * 50, (e[0]![1] + e[1]![1]) * 50 + 10]);
    extra = `<g data-extra="freckles">${dots.map(([x, y]) => `<circle cx="${x}" cy="${y}" r=".85" fill="#7c4d32" opacity=".65"/>`).join('')}</g>`;
  }
  // `Acts.preview` is never set in the app, so painted hair is what decides.
  return `${eyes}${activity.hairStyles ? '' : hair}${extra}`;
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

export interface PaintedOptions {
  size?: number;
  title?: string;
  /** An activity, or its id. Defaults to the first of FIRST, as the display does. */
  activity?: Activity | string;
  /** Crop to the head, for thumbnails and faces. */
  headOnly?: boolean;
  /**
   * How a head is cropped. 'face' (the default) is the same square around the
   * face for everybody, so every face fills its circle the same way and long
   * hair is cut at the edge. 'hair' widens it to the whole hairstyle -- the
   * builder's hair choices, where the hair is the point.
   */
  crop?: 'face' | 'hair';
  /** Seeds this character's motion, so two children never blink together. */
  key?: string;
}

/** A painted character, as an SVG string. `Avatar.render(spec, {mode: 'full'})`. */
export function render(spec: Partial<AvatarSpec> | null | undefined, options: PaintedOptions = {}): string {
  const s = normalise(spec);
  const asked = options.activity;
  let activity: Activity =
    (asked && BY_ID[typeof asked === 'string' ? asked : asked.id]) || BY_ID[FIRST[0]!]!;
  const outfit = outfitOf(spec);
  const data = artFor(activity.id, outfit);
  if (!data) {
    return `<svg class="avatar avatar-idle avatar-loading" viewBox="0 0 100 140" width="${options.size || 160}" role="img" aria-label="Loading"></svg>`;
  }
  activity = data.activity;
  // A HEAD IS ONE FRAME (audit, 2026-10-12). The standing portrait holds
  // frame 0 anyway (see pose), so the other seven frames of a 36px face in a
  // list were never shown -- yet each one shipped ~35 images and ~350 SVG
  // nodes, and Home downloaded the whole body loop for every face on it.
  const frames = options.headOnly ? data.frames.slice(0, 1) : data.frames;
  const uid = 'iv' + ++serial, { w, h } = data.frames[0]!;
  const size = options.size || 160;
  const skin = catalogue.skin!.swatches![s.skin]!;
  const hairColor = catalogue.hairColor!.swatches![s.hairColor]!;
  const cloth = catalogue.shirtColor!.swatches![s.shirtColor]!;
  const eyeColor = catalogue.eyeColor!.swatches![s.eyeColor]!;
  const shortHair = activity.interaction === 'stroke-rabbit' && s.hair === 2 && activity.ear;
  const shortHairMask = shortHair ? `<mask id="${uid}shortHair" maskUnits="userSpaceOnUse" x="${-w}" y="${-h}" width="${w * 3}" height="${h * 3}" style="mask-type:alpha"><rect x="${-w}" y="${-h}" width="${w * 3}" height="${h + (activity.ear![1] - activity.ear![3] * .5) * h}" fill="white"/><ellipse cx="${(activity.ear![0] - activity.ear![2] * .4) * w}" cy="${(activity.ear![1] - activity.ear![3] * .5) * h}" rx="${activity.ear![2] * w * 2.2}" ry="${activity.ear![3] * h * .8}" fill="white"/>${[data.frames[0]!.props, data.frames[0]!.skin].filter(Boolean).map(url => `<image href="${url}" x="0" y="0" width="${w}" height="${h}"/>`).join('')}</mask>` : '';
  const longHair = activity.interaction === 'stroke-rabbit' && [3, 8, 9].includes(s.hair);
  // Extend the inner hair edge behind the held head. The head clip preserves
  // the original outer curls while the body covers the expanded face opening.
  const hairJoinDefs = longHair ? `<filter id="${uid}hairJoin" x="-30%" y="-30%" width="160%" height="160%"><feMorphology operator="dilate" radius="${w / 70}"/></filter>` : '';
  const defs = `<defs>${tint(uid + 'skin', skin, 1.10)}${tint(uid + 'cloth', cloth, 1.42)}${tint(uid + 'hair', hairColor, 2.05, activity.interaction === 'stroke-rabbit')}${tint(uid + 'brows', hairColor, 1.5)}${tint(uid + 'tongue', '#c8787c', 1.15)}${shortHairMask}${hairJoinDefs}</defs>`;
  const hairFor = (a: Activity) => {
    const originalFit = a.hairFit;
    // Eye tilt exaggerates this quarter-view skull's rotation. Register the
    // actual forehead and temple instead, keeping the ear within the cutout.
    const fit = originalFit && a.interaction === 'stroke-rabbit'
      ? { ...originalFit, scale: originalFit.scale * ([0, 6, 7].includes(s.hair) ? .94 : 1), rotation: (originalFit.rotation ?? 0) + 20, targetX: (originalFit.targetX ?? a.head.x) + 18 / a.canvas![0]!, targetY: (originalFit.targetY ?? a.head.y) + 26 / a.canvas![1]! }
      : originalFit;
    const hairTransform = fit ? ` transform="translate(${(fit.targetX ?? a.head.x) * w} ${(fit.targetY ?? a.head.y) * h})${fit.rotation ? ` rotate(${fit.rotation})` : ''} scale(${fit.scale}) translate(${-fit.fromX * w} ${-fit.fromY * h})"` : '';
    return a.hairStyles?.includes(s.hair)
    ? `${shortHair ? `<g mask="url(#${uid}shortHair)">` : ''}<image data-painted-hair="${s.hair}" href="${hairUrl(activity.id, s.hair)}" x="0" y="0" width="${w}" height="${h}" filter="url(#${uid}hair)"${hairTransform}/>${shortHair ? '</g>' : ''}` : '';
  };
  const headFor = (a: Activity, eyeUid: string) => {
    const head = a.head, hw = head.scale * w, hx = head.x * w, hy = head.y * h;
    return `<g data-bone="head" transform="translate(${hx.toFixed(2)} ${hy.toFixed(2)}) rotate(${head.angle})">` +
      `<g transform="scale(${(hw / 100).toFixed(4)}) translate(-50 -50)">${headOverlay(s, a, eyeUid, hairColor, eyeColor)}</g></g>`;
  };
  // Keep long hair behind the stroking arm and rabbit, with the fringe in front.
  const pettingHair = (a: Activity, eyeUid: string, back: boolean) => {
    // The side locks continue below the ear. Only the trailing length goes
    // behind the shoulder; clipping at eye level left a bare temple.
    const cut = a.eyes.reduce((sum, eye) => sum + eye[1], 0) / a.eyes.length * 100 + 28;
    const head = a.head, hw = head.scale * w, id = eyeUid + (back ? 'hairBack' : 'hairFront');
    const transform = `translate(${head.x * w} ${head.y * h}) rotate(${head.angle}) scale(${hw / 100}) translate(-50 -50)`;
    // Overlap the antialiased clips so their shared edge cannot open a seam.
    return `<defs><clipPath id="${id}" clipPathUnits="userSpaceOnUse"><rect x="-300" y="${back ? cut - 2 : -500}" width="700" height="${back ? 1000 : cut + 502}" transform="${transform}"/></clipPath></defs><g data-hair-depth="${back ? 'back' : 'front'}" clip-path="url(#${id})">${hairFor(a)}</g>`;
  };
  const paintedHair = hairFor(activity);
  const pettingEar = (a: Activity, f: Frame, eyeUid: string) => {
    if (!a.ear) return '';
    const [x, y, rx, ry] = a.ear, id = eyeUid + 'ear';
    // Reuse the painted ear above the hair, including its original outline.
    return `<defs><clipPath id="${id}"><path d="M-.42-.86 C.1-1.02 .75-.79 .89-.26 C1.06 .32 .65 .91-.02 .96 C-.44 1.01-.84 .7-.82 .45 C-.56 .23-.63-.47-.42-.86Z" transform="translate(${x * w} ${y * h}) scale(${rx * w} ${ry * h})"/></clipPath></defs><g data-ear-depth="front" clip-path="url(#${id})"><g style="isolation:isolate">${f.props ? `<image style="mix-blend-mode:plus-lighter" href="${f.props}" x="0" y="0" width="${w}" height="${h}"/>` : ''}${f.skin ? `<image style="mix-blend-mode:plus-lighter" href="${f.skin}" x="0" y="0" width="${w}" height="${h}" filter="url(#${uid}skin)"/>` : ''}</g></g>`;
  };
  const hairUnderlay = (a: Activity, eyeUid: string) => {
    if (!longHair) return '';
    const head = a.head, hw = head.scale * w, id = eyeUid + 'hairUnderlay';
    return `<defs><clipPath id="${id}"><ellipse cx="51" cy="76" rx="60" ry="54" transform="translate(${head.x * w} ${head.y * h}) rotate(${head.angle}) scale(${hw / 100}) translate(-50 -50)"/></clipPath></defs><g data-hair-underlay="" clip-path="url(#${id})"><g filter="url(#${uid}hairJoin)">${hairFor(a)}</g></g>`;
  };
  const framed = Boolean(activity.poseFits?.length);
  const moving = !options.headOnly && Boolean(activity.playback?.interpolate && activity.transitions);
  const firstMap = activity.transitions?.['0'];
  const motionDefs = moving ? `<defs><filter id="${uid}motion" filterUnits="userSpaceOnUse" primitiveUnits="userSpaceOnUse" x="${-w * .25}" y="${-h * .25}" width="${w * 1.5}" height="${h * 1.5}" color-interpolation-filters="sRGB"><feFlood flood-color="#808080" result="neutral"/><feImage data-motion-map="" href="${motionUrl(activity.id, firstMap!.backward, activity.motionRoot)}" x="0" y="0" width="${w}" height="${h}" preserveAspectRatio="none" result="motion"/><feMerge result="field"><feMergeNode in="neutral"/><feMergeNode in="motion"/></feMerge><feComponentTransfer in="field" result="centered"><feFuncR type="linear" slope="1" intercept="-0.00196078431372549"/><feFuncG type="linear" slope="1" intercept="-0.00196078431372549"/></feComponentTransfer><feDisplacementMap data-motion-displacement="" in="SourceGraphic" in2="centered" scale="0" xChannelSelector="R" yChannelSelector="G"/></filter></defs>` : '';
  // Add the disjoint masks inside an isolated body group, then composite that
  // whole group normally over back hair. Adding the group to back hair creates
  // bright orange patches where the garment and curls overlap.
  const layers = (f: Frame, i: number) => `<g data-frame="${i}" style="${framed ? 'isolation:isolate' : 'mix-blend-mode:plus-lighter;isolation:isolate'}"${i ? ' opacity="0"' : ''}>` +
    (f.companion ? `<image data-companion="${activity.companion!.species}" href="${f.companion}" x="0" y="0" width="${w}" height="${h}"/>` : '') +
    (framed && activity.interaction === 'stroke-rabbit' ? pettingHair({ ...activity, ...activity.poseFits![i] }, uid + 'f' + i, true) + hairUnderlay({ ...activity, ...activity.poseFits![i] }, uid + 'f' + i) : '') +
    (framed ? `<g data-painted-body="" style="isolation:isolate"${moving && activity.interaction === 'stroke-rabbit' ? ` filter="url(#${uid}motion)"` : ''}>` : '') +
    (f.props ? `<image style="mix-blend-mode:plus-lighter" href="${f.props}" x="0" y="0" width="${w}" height="${h}"/>` : '') +
    (f.garment ? `<image style="mix-blend-mode:plus-lighter" href="${f.garment}" x="0" y="0" width="${w}" height="${h}" filter="url(#${uid}cloth)"/>` : '') +
    (f.skin ? `<image style="mix-blend-mode:plus-lighter" href="${f.skin}" x="0" y="0" width="${w}" height="${h}" filter="url(#${uid}skin)"/>` : '') +
    (f.brows ? `<image style="mix-blend-mode:plus-lighter" href="${f.brows}" x="0" y="0" width="${w}" height="${h}" filter="url(#${uid}brows)"/>` : '') +
    (f.tongue ? `<image style="mix-blend-mode:plus-lighter" href="${f.tongue}" x="0" y="0" width="${w}" height="${h}" filter="url(#${uid}tongue)"/>` : '') +
    (framed ? `</g>${headFor({ ...activity, ...activity.poseFits![i] }, uid + 'f' + i)}${activity.interaction === 'stroke-rabbit' ? pettingHair({ ...activity, ...activity.poseFits![i] }, uid + 'f' + i, false) + pettingEar(activity, f, uid + 'f' + i) : hairFor({ ...activity, ...activity.poseFits![i] })}` : '') + `</g>`;
  const head = activity.head, hw = head.scale * w, hx = head.x * w, hy = head.y * h;
  const headGroup = headFor(activity, uid);
  const title = options.title ? `<title>${String(options.title).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]!)}</title>` : '';
  const canvas = activity.canvas as number[] | undefined;
  let box = activity.bounds ? (activity.bounds as number[]).map((v) => (v * w) / canvas![0]!) : [0, 0, w, h];
  const hairBounds = activity.hairBounds?.[s.hair as unknown as string] as number[] | undefined;
  if (paintedHair && hairBounds) {
    const hb = hairBounds.map((v) => (v * w) / canvas![0]!);
    const left = Math.min(box[0]!, hb[0]! - 3), top = Math.min(box[1]!, hb[1]! - 3);
    box = [left, top, Math.max(box[0]! + box[2]!, hb[2]! + 3) - left, Math.max(box[1]! + box[3]!, hb[3]! + 3) - top];
  }
  if (options.headOnly && options.crop !== 'hair') {
    // One square around the face, whatever the hair (2026-10-13): sized from
    // the hair's bounds, a curly head drew its face half the size of a
    // short-haired one in the same circle.
    const side = hw * 1.22;
    box = [hx - side / 2, hy - hw * 0.66, side, side];
  } else if (options.headOnly) {
    const hb = hairBounds?.map((v) => (v * w) / canvas![0]!);
    const left = Math.min(hx - hw * 0.70, hb ? hb[0]! - 3 : Infinity);
    const top = Math.min(hy - hw * 0.78, hb ? hb[1]! - 3 : Infinity);
    const right = Math.max(hx + hw * 0.70, hb ? hb[2]! + 3 : -Infinity);
    box = [left, top, right - left, hy + hw * 0.66 - top];
  }
  const serialised = JSON.stringify(s).replace(/"/g, '&quot;');
  return `<svg class="avatar avatar-idle${data.placeholder ? ' avatar-placeholder-art' : ''}" data-idle="12" data-motion-key="${hash(options.key || uid)}" data-activity="${activity.id}" data-outfit="${outfit}"${activity.interaction ? ` data-interaction="${activity.interaction}"` : ''} data-source-width="${w}" data-source-height="${h}" data-frames="${frames.length}"${options.headOnly ? ' data-head="true"' : ''} data-spec="${serialised}" ` +
    `viewBox="${box.join(' ')}" width="${size}" height="${((size * box[3]!) / box[2]!).toFixed(1)}" role="img" aria-hidden="${!options.title}" focusable="false">` +
    `${title}${defs}${motionDefs}<g data-bone="root"><g data-bone="breathe"><g data-frame-composite="${framed ? 'single' : 'additive'}" style="isolation:isolate"${moving && activity.interaction !== 'stroke-rabbit' ? ` filter="url(#${uid}motion)"` : ''}>${frames.map(layers).join('')}</g>${framed ? '' : headGroup + paintedHair}</g></g></svg>`;
}

/**
 * Just the head: `AvatarIdle.renderHead`. The display draws it as the standing
 * portrait cropped to the head, in outfit 0, whenever that painting is ready --
 * which here it always is. Used for every face: builder thumbnails, the
 * family list, beside goals and journal rows.
 */
export function renderHead(spec: Partial<AvatarSpec> | null | undefined, options: PaintedOptions = {}): string {
  return render({ ...spec, outfit: 0 }, { ...options, activity: 'standing', headOnly: true });
}

// ---------------------------------------------------------------------------
// The only motion
// ---------------------------------------------------------------------------

const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);

interface Rig {
  activity: Activity;
  seed: number;
  count: number;
  frames: Element[];
  eyes: Element | null;
  reveals: Element[];
  lids: Element[];
  breathe: Element | null;
  root: Element | null;
  lastFrame?: number;
  motionImage: Element | null;
  displacement: Element | null;
  lastMap?: string;
  lastBlinkOpenness?: number;
}

const rigs = new WeakMap<SVGSVGElement, Rig>();

/** `pose(svg, time)` -- whole-frame interpolation, breathing, sway and blink. */
export function pose(svg: SVGSVGElement, time: number, options: { frame?: number } = {}): void {
  let r = rigs.get(svg);
  if (!r) {
    const outfit = +(svg.dataset.outfit ?? 0);
    const activity = artFor(svg.dataset.activity ?? '', outfit)?.activity || BY_ID[svg.dataset.activity ?? '']!;
    const seed = hash((svg.dataset.motionKey || svg.dataset.spec) + (svg.dataset.activity ?? ''));
    r = {
      activity, seed, count: +(svg.dataset.frames ?? 1),
      frames: [...svg.querySelectorAll('[data-frame]')],
      eyes: svg.querySelector('[data-bone="eyes"]'),
      reveals: [...svg.querySelectorAll('[data-eye-reveal]')],
      lids: [...svg.querySelectorAll('[data-upper-lid]')],
      breathe: svg.querySelector('[data-bone="breathe"]'),
      root: svg.querySelector('[data-bone="root"]'),
      motionImage: svg.querySelector('[data-motion-map]'),
      displacement: svg.querySelector('[data-motion-displacement]'),
    };
    rigs.set(svg, r);
  }
  const a = r.activity, hold = a.hold, seed = r.seed;
  svg.dataset.motionTime = time.toFixed(3);
  const span = hold[0] + ((seed % 1000) / 1000) * (hold[1] - hold[0]);
  const fade = Math.min(a.expression === 'asleep' || a.posture === 'lying' ? 0.4 : 0.24, span * 0.25);
  // Which held frame, and how far into the dissolve.
  if (a.playback && r.count > 1) {
    const order = a.playback.order;
    const cadence = .94 + (seed % 101) / 100 * .12;
    const ticks = time * a.playback.fps * cadence + (seed % 997) / 997 * order.length;
    const tick = ((ticks % order.length) + order.length) % order.length;
    let frame = Number.isInteger(options.frame) ? clamp(options.frame!, 0, r.count - 1) : order[Math.floor(tick)]!;
    let amount = 0;
    if (a.interaction === 'stroke-rabbit' && !Number.isInteger(options.frame)) {
      // One opaque contact drawing and one measured hand field: opposite
      // correspondence maps do not agree at the old mid-pose image swap.
      frame = 0;
      const stroke = .5 - .5 * Math.cos(Math.PI * 2 * (time / 3 + (seed % 997) / 997));
      const pair = a.transitions?.['0'];
      if (pair) {
        const map = motionUrl(a.id, pair.backward, a.motionRoot);
        if (r.lastMap !== map) { r.motionImage?.setAttribute('href', map); r.lastMap = map; }
        amount = stroke * .5 * pair.scale * (+(svg.dataset.sourceWidth ?? 0) / a.canvas![0]!);
      }
      svg.dataset.poseProgress = (stroke * .5).toFixed(4);
    } else if ((a.id === 'standing' || a.posture === 'lying') && !Number.isInteger(options.frame)) {
      // Idle/resting drawings differ in face and clothing contours. Hold one
      // coherent pose and breathe continuously instead of warping between them.
      frame = 0;
      svg.dataset.poseProgress = '0.0000';
    } else if (a.playback.interpolate && a.transitions && !Number.isInteger(options.frame)) {
      // One eased out-and-back trajectory, rather than stopping at every
      // drawing. Rest only at the ends; both ends join with zero velocity.
      const end = r.count - 1, reverse = end + 2;
      const position = tick < 1 ? 0 : tick < end + 1 ? end * (.5 - .5 * Math.cos(Math.PI * (tick - 1) / end)) : tick < reverse ? end : end * (.5 + .5 * Math.cos(Math.PI * (tick - reverse) / end));
      const lower = Math.min(end - 1, Math.floor(position)), phase = position - lower;
      frame = phase < .5 ? lower : lower + 1;
      const pair = a.transitions[String(lower)]!;
      const map = motionUrl(a.id, frame === lower ? pair.backward : pair.forward, a.motionRoot);
      if (r.lastMap !== map) { r.motionImage?.setAttribute('href', map); r.lastMap = map; }
      amount = (frame === lower ? phase : 1 - phase) * pair.scale * (+(svg.dataset.sourceWidth ?? 0) / a.canvas![0]!);
      svg.dataset.poseProgress = position.toFixed(4);
    }
    r.displacement?.setAttribute('scale', amount.toFixed(4));
    if (r.lastFrame !== frame) {
      r.frames.forEach((g, j) => g.setAttribute('opacity', j === frame ? '1' : '0'));
      svg.dataset.poseFrame = String(frame);
      r.lastFrame = frame;
    }
  } else if (r.count > 1) {
    const t = (time + ((seed % 997) / 997) * span) / span, i = Math.floor(t), u = t - i;
    const cur = i % r.count, nxt = (i + 1) % r.count;
    const phase = u > 1 - fade / span ? clamp((u - (1 - fade / span)) / (fade / span), 0, 1) : 0;
    const mix = phase * phase * (3 - 2 * phase);
    r.frames.forEach((g, j) => g.setAttribute('opacity', String(j === cur ? 1 - mix : j === nxt ? mix : 0)));
  }
  // A resting child breathes gently about the ground line; a large whole-body
  // stretch makes their head and legs visibly rock against the grass.
  const slow = a.expression === 'asleep' || a.posture === 'lying';
  const rate = slow ? 6.8 : a.id === 'standing' ? 5.6 : 3.9, depth = a.interaction === 'stroke-rabbit' ? 0 : slow ? 0.0025 : a.id === 'standing' ? 0.0035 : 0.006;
  const breath = 1 + depth * Math.sin((time * Math.PI * 2) / rate + (seed % 628) / 100);
  if (r.breathe) {
    r.breathe.setAttribute('transform',
      `translate(0 ${(1 - breath) * (a.ground || 0.9) * (+(svg.dataset.sourceHeight ?? 0) || 400)}) scale(1 ${breath.toFixed(5)})`);
  }
  if (a.id === 'standing' && r.root) {
    const angle = 0.55 * Math.sin((time / (9 + (seed % 400) / 100)) * Math.PI * 2 + (seed % 17));
    r.root.setAttribute('transform',
      `rotate(${angle.toFixed(3)} ${+(svg.dataset.sourceWidth ?? 0) * 0.5} ${+(svg.dataset.sourceHeight ?? 0) * (a.ground ?? 0)})`);
  }
  // Blink: 120ms, per-child interval, skipped when the eyes are painted shut.
  if (r.eyes && a.expression !== 'asleep') {
    const interval = 3 + (seed % 4001) / 1000, bt = (((time + (seed % 1379) / 200) % interval) + interval) % interval;
    const lid = Number.isInteger(options.frame) ? 1 : bt < 0.12 ? Math.abs(bt - 0.06) / 0.06 : 1;
    // The rig belongs to this immutable SVG, so clones start uncached.
    if (r.lastBlinkOpenness !== lid) {
      const closure = 1 - lid;
      r.reveals.forEach((rect) => {
        rect.setAttribute('y', String(-7 + 14 * closure));
        rect.setAttribute('height', String(14 * lid));
      });
      r.lids.forEach((path) => path.setAttribute('d', `M-6 0 C-6 ${-8.6 + 14 * closure} 5.8 ${-8.8 + 14.3 * closure} 6-.4`));
      r.lastBlinkOpenness = lid;
    }
  }
}

/**
 * One clock for every painted character on the page, as the display runs it:
 * any `svg[data-idle="12"]` that appears is picked up and posed each frame, and
 * dropped when it leaves the document. A hidden tab, `prefers-reduced-motion`
 * or an ancestor with `.preview-paused` or `.paused` holds its clock.
 */
let ticking = false;
let animationFrame = 0;
let observer: MutationObserver | undefined;
export function stopMotion(): void {
  ticking = false;
  if (animationFrame) cancelAnimationFrame(animationFrame);
  animationFrame = 0;
  observer?.disconnect();
  observer = undefined;
}
// Vite replaces this module during art reviews. Retiring its previous clock
// prevents old RAF loops from advancing the same resident several times.
if (import.meta.hot) import.meta.hot.dispose(stopMotion);
export function startMotion(): void {
  if (ticking || typeof document === 'undefined' || typeof requestAnimationFrame === 'undefined') return;
  ticking = true;
  const live = new Set<SVGSVGElement & { _t?: number }>();
  const reduced = typeof matchMedia !== 'undefined' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
  const find = (n: Node) => {
    if (n.nodeType !== 1) return;
    const el = n as Element;
    if (el.matches('svg[data-idle="12"]')) { prepareSvg(el as SVGSVGElement); live.add(el as SVGSVGElement); }
    el.querySelectorAll('svg[data-idle="12"]').forEach((svg) => { prepareSvg(svg as SVGSVGElement); live.add(svg as SVGSVGElement); });
  };
  observer = new MutationObserver((records) => records.forEach((r) => r.addedNodes.forEach(find)));
  observer.observe(document.documentElement, { childList: true, subtree: true });
  find(document.documentElement);
  let last = 0;
  animationFrame = requestAnimationFrame(function tick(ms) {
    if (!ticking) return;
    const dt = Math.min(0.06, (ms - last) / 1000) || 0;
    last = ms;
    for (const svg of live) {
      if (!svg.isConnected) {
        live.delete(svg);
        continue;
      }
      if (svg.dataset.external === 'true' || document.hidden) continue;
      if (!(reduced?.matches || svg.closest('.preview-paused,.paused'))) svg._t = (svg._t || 0) + dt;
      pose(svg, svg._t || 0);
    }
    animationFrame = requestAnimationFrame(tick);
  });
}
